<div class="onboarding-requests-container">

  <!-- ═══════════════ HEADER & CONTROLS ═══════════════ -->
  <div class="row g-3 align-items-center mb-4">
    <div class="col-12 col-lg-4">
      <div class="status-tab-group">
        <button type="button" class="status-tab-btn" [class.active]="selectedStatusTab === 'Pending'"
          (click)="switchStatusTab('Pending')">
          <i class="bi bi-hourglass-split me-1"></i> Pending
          <span class="badge rounded-pill bg-warning text-dark ms-1" *ngIf="pendingCount > 0">{{ pendingCount }}</span>
        </button>
        <button type="button" class="status-tab-btn" [class.active]="selectedStatusTab === 'Approved'"
          (click)="switchStatusTab('Approved')">
          <i class="bi bi-check2-circle me-1"></i> Approved
        </button>
        <button type="button" class="status-tab-btn" [class.active]="selectedStatusTab === 'All'"
          (click)="switchStatusTab('All')">
          All ({{ allRequests.length }})
        </button>
      </div>
    </div>

    <!-- Search -->
    <div class="col-12 col-md-7 col-lg-5">
      <div class="nx-search">
        <i class="bi bi-search"></i>
        <input type="text" placeholder="Search by name, phone, email or role..." [(ngModel)]="searchTerm"
          (ngModelChange)="onSearchChange()" />
        <button type="button" class="nx-search-clear" *ngIf="searchTerm" (click)="searchTerm = ''; onSearchChange()"
          aria-label="Clear search">
          <i class="bi bi-x-lg"></i>
        </button>
      </div>
    </div>

    <!-- Role filter -->
    <div class="col-12 col-md-5 col-lg-3">
      <div class="d-flex align-items-center gap-2">
        <label class="nx-filter-label">Role</label>
        <select class="nx-select" [(ngModel)]="selectedAffiliationFilter" (ngModelChange)="onAffiliationFilterChange()">
          <option *ngFor="let opt of affiliationOptions" [value]="opt.value">{{ opt.label }}</option>
        </select>
      </div>
    </div>
  </div>

  <!-- ═══════════════ LOADING ═══════════════ -->
  <div *ngIf="isLoading" class="text-center py-5">
    <div class="spinner-border nx-spinner" role="status"><span class="visually-hidden">Loading...</span></div>
    <p class="mt-2 text-muted small">Loading onboarding requests...</p>
  </div>

  <!-- ═══════════════ EMPTY ═══════════════ -->
  <div *ngIf="!isLoading && filteredRequests.length === 0"
    class="d-flex flex-column align-items-center justify-content-center py-5">
    <div class="bg-light rounded-circle p-4 mb-3">
      <i class="bi bi-person-check text-muted" style="font-size: 3rem"></i>
    </div>
    <h5 class="fw-bold text-dark">No {{ selectedStatusTab === 'Pending' ? 'pending ' : '' }}requests found</h5>
    <p class="text-muted text-center" style="max-width: 380px">
      {{ searchTerm ? 'Try changing your search or role filter.' : 'When people submit the self-onboarding form, their requests will appear here for approval.' }}
    </p>
  </div>

  <!-- ═══════════════ TABLE ═══════════════ -->
  <div *ngIf="!isLoading && filteredRequests.length > 0" class="card shadow-sm border-0 rounded-4 overflow-hidden">
    <div class="table-responsive">
      <table class="table table-hover align-middle mb-0 nx-table">
        <thead>
          <tr>
            <th style="width: 70px">Sr No</th>
            <th>User</th>
            <th>Contact</th>
            <th>Role</th>
            <th *ngIf="module === 'hospital'">Department</th>
            <th *ngIf="module === 'society'">Building / Flat</th>
            <th>Submitted</th>
            <th>Status</th>
            <th class="text-end" style="min-width: 170px">Action</th>
          </tr>
        </thead>
        <tbody>
          <tr *ngFor="let req of paginatedRequests; let i = index">
            <td class="text-muted small">{{ startRecordIndex + i }}</td>

            <td>
              <div class="d-flex align-items-center gap-2">
                <div class="avatar-circle">{{ (req.firstName.charAt(0) || 'U') | uppercase }}</div>
                <div>
                  <div class="fw-semibold text-custom-blue">{{ req.firstName }} {{ req.middleName }} {{ req.lastName }}</div>
                  <div class="text-muted small" *ngIf="req.designation">{{ req.designation }}</div>
                </div>
              </div>
            </td>

            <td class="small">
              <div><i class="bi bi-telephone me-1 nx-accent"></i>{{ req.mobileNo || '-' }}</div>
              <div class="text-muted text-truncate" style="max-width: 200px" *ngIf="req.emailId">
                <i class="bi bi-envelope me-1"></i>{{ req.emailId }}
              </div>
            </td>

            <td><span class="role-badge">{{ roleLabel(req.affiliationType) }}</span></td>

            <td *ngIf="module === 'hospital'" class="small text-muted">
              {{ req.department || '-' }}
              <span class="d-block" *ngIf="req.specialization">({{ req.specialization }})</span>
            </td>

            <td *ngIf="module === 'society'" class="small text-muted">
              <span *ngIf="req.buildingName || req.buildingId">
                {{ req.buildingName || getBuildingName(req.buildingId) || ('Building ' + req.buildingId) }}
              </span>
              <span class="d-block" *ngIf="req.flatNumber || req.flatId">Flat {{ req.flatNumber || req.flatId }}</span>
              <span *ngIf="!req.buildingId && !req.flatId">-</span>
            </td>

            <td class="small text-muted">{{ req.submittedOn ? (req.submittedOn | date:'dd MMM yyyy') : '-' }}</td>

            <td>
              <span class="status-pill" [class.pending]="req.status === 'Pending'" [class.approved]="req.status !== 'Pending'">
                <i class="bi" [ngClass]="req.status === 'Pending' ? 'bi-hourglass-split' : 'bi-check-circle-fill'"></i>
                {{ req.status }}
              </span>
            </td>

            <td class="text-end">
              <button *ngIf="req.status === 'Pending'" type="button" class="nx-btn nx-btn-primary nx-btn-sm"
                (click)="openReviewModal(req)">
                <i class="bi bi-shield-check me-1"></i> Review &amp; Approve
              </button>
              <button *ngIf="req.status !== 'Pending'" type="button" class="nx-btn nx-btn-outline nx-btn-sm"
                (click)="openReviewModal(req)">
                <i class="bi bi-eye me-1"></i> View
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div class="d-flex flex-wrap align-items-center justify-content-between p-3 border-top gap-2 nx-pagebar">
      <div class="d-flex flex-wrap align-items-center gap-2 small text-muted">
        <span>Showing <strong>{{ startRecordIndex }}</strong> - <strong>{{ endRecordIndex }}</strong> of
          <strong>{{ filteredRequests.length }}</strong></span>
        <span class="d-none d-sm-inline">|</span>
        <span>Per page</span>
        <select class="nx-select nx-select-sm" [ngModel]="pageSize" (ngModelChange)="onPageSizeChange($event)">
          <option *ngFor="let s of pageSizeOptions" [value]="s">{{ s }}</option>
        </select>
      </div>

      <nav aria-label="Page navigation" *ngIf="totalPages > 1">
        <ul class="pagination pagination-sm mb-0 nx-pagination">
          <li class="page-item" [class.disabled]="currentPage === 1">
            <a class="page-link" href="javascript:void(0);" (click)="goToPage(currentPage - 1)">Previous</a>
          </li>
          <li class="page-item" *ngFor="let p of pageNumbers" [class.active]="currentPage === p">
            <a class="page-link" href="javascript:void(0);" (click)="goToPage(p)">{{ p }}</a>
          </li>
          <li class="page-item" [class.disabled]="currentPage === totalPages">
            <a class="page-link" href="javascript:void(0);" (click)="goToPage(currentPage + 1)">Next</a>
          </li>
        </ul>
      </nav>
    </div>
  </div>

  <!-- ═══════════════ REVIEW MODAL ═══════════════ -->
  <ng-template #previewModal>
    <div class="form-container">

      <div class="modal-header">
        <div>
          <h4 class="modal-title font-weight-600">{{ isViewOnly ? 'Request Details' : 'Review & Approve Request' }}</h4>
          <small class="text-muted" *ngIf="selectedRequest">
            {{ roleLabel(selectedRequest.affiliationType) }}
            <span *ngIf="selectedRequest.submittedOn"> · Submitted {{ selectedRequest.submittedOn | date:'dd MMM yyyy' }}</span>
          </small>
        </div>
        <button type="button" class="btn-close" aria-label="Close" (click)="closeModal()"></button>
      </div>

      <form [formGroup]="reviewForm" autocomplete="off" (ngSubmit)="submitApproval()">
        <div class="modal-body">

          <!-- Personal -->
          <h6 class="section-heading">Personal Details</h6>

          <div class="form-row">
            <div class="form-group fg-third">
              <label>First Name <span class="req">*</span></label>
              <input type="text" class="form-control" formControlName="firstName" placeholder="First Name" />
              <small class="text-danger" *ngIf="reviewForm.get('firstName')?.hasError('required') && reviewForm.get('firstName')?.touched">
                First Name is required.
              </small>
            </div>
            <div class="form-group fg-third">
              <label>Middle Name</label>
              <input type="text" class="form-control" formControlName="middleName" placeholder="Middle Name" />
            </div>
            <div class="form-group fg-third">
              <label>Last Name <span class="req">*</span></label>
              <input type="text" class="form-control" formControlName="lastName" placeholder="Last Name" />
              <small class="text-danger" *ngIf="reviewForm.get('lastName')?.hasError('required') && reviewForm.get('lastName')?.touched">
                Last Name is required.
              </small>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group fg-half">
              <label>Mobile Number <span class="req">*</span></label>
              <input type="tel" class="form-control" formControlName="mobileNo" maxlength="10"
                placeholder="10-digit mobile number" inputmode="numeric" />
              <small class="text-danger" *ngIf="reviewForm.get('mobileNo')?.touched && reviewForm.get('mobileNo')?.errors">
                <span *ngIf="reviewForm.get('mobileNo')?.hasError('required')">Mobile number is required.</span>
                <span *ngIf="reviewForm.get('mobileNo')?.hasError('pattern')">Enter a valid 10-digit mobile number.</span>
              </small>
            </div>
            <div class="form-group fg-half">
              <label>Email</label>
              <input type="email" class="form-control" formControlName="emailId" placeholder="Email address" />
              <small class="text-danger" *ngIf="reviewForm.get('emailId')?.hasError('email') && reviewForm.get('emailId')?.touched">
                Enter a valid email address.
              </small>
            </div>
          </div>

          <div class="form-row">
            <div class="form-group fg-third">
              <label>Gender</label>
              <select class="form-control form-select" formControlName="gender">
                <option value="">Select Gender</option>
                <option value="Male">Male</option>
                <option value="Female">Female</option>
                <option value="Other">Other</option>
              </select>
            </div>
            <div class="form-group fg-third">
              <label>Date of Birth</label>
              <input type="date" class="form-control" formControlName="dob" />
            </div>
            <div class="form-group fg-third">
              <label>Designation</label>
              <input type="text" class="form-control" formControlName="designation" placeholder="Designation" />
            </div>
          </div>

          <div class="form-row">
            <div class="form-group fg-wide">
              <label>Address</label>
              <input type="text" class="form-control" formControlName="address" placeholder="Street / Area" />
            </div>
            <div class="form-group fg-narrow">
              <label>PIN Code</label>
              <input type="text" class="form-control" formControlName="pinCode" maxlength="6" placeholder="6-digit PIN"
                inputmode="numeric" />
              <small class="text-danger" *ngIf="reviewForm.get('pinCode')?.hasError('pattern') && reviewForm.get('pinCode')?.touched">
                Enter a valid PIN code.
              </small>
            </div>
          </div>

          <!-- Hospital -->
          <ng-container *ngIf="module === 'hospital'">
            <h6 class="section-heading mt-3">Hospital Details</h6>
            <div class="form-row">
              <div class="form-group fg-half">
                <label>Department</label>
                <select class="form-control form-select" formControlName="department">
                  <option value="">Select Department</option>
                  <option *ngFor="let dept of departments" [value]="dept.departmentName || dept.name">
                    {{ dept.departmentName || dept.name }}
                  </option>
                </select>
              </div>
              <div class="form-group fg-half" *ngIf="selectedRequest?.affiliationType === 'Doctor'">
                <label>Specialization</label>
                <input type="text" class="form-control" formControlName="specialization" placeholder="e.g. Cardiologist" />
              </div>
            </div>
          </ng-container>

          <!-- Society -->
          <ng-container *ngIf="module === 'society'">
            <h6 class="section-heading mt-3">Society Details</h6>
            <div class="form-row">
              <div class="form-group fg-half">
                <label>Building / Wing</label>
                <select class="form-control form-select" formControlName="buildingId">
                  <option [ngValue]="0">Select Building</option>
                  <option *ngFor="let b of buildings" [ngValue]="b.buildingId">{{ b.buildingName }}</option>
                </select>
              </div>
              <div class="form-group fg-half">
                <label>
                  Flat
                  <span *ngIf="flatsLoading" class="spinner-border spinner-border-sm nx-spinner ms-1"></span>
                </label>
                <select class="form-control form-select" formControlName="flatId">
                  <option [ngValue]="0">{{ reviewForm.controls.buildingId.value ? 'Select Flat' : 'Select Building First' }}</option>
                  <option *ngFor="let f of flats" [ngValue]="f.flatId">{{ f.flatNumber || f.flatNo }}</option>
                </select>
              </div>
            </div>
          </ng-container>

          <!-- Credentials (pending requests only) -->
          <div class="credentials-card mt-4" *ngIf="!isViewOnly">
            <div class="credentials-header">Assign Credentials</div>

            <div class="credentials-body">

              <!-- Username -->
              <div class="credentials-group">
                <label>Username <span class="req">*</span></label>
                <div class="input-with-status">
                  <input type="text" formControlName="userName" placeholder="Enter Username" class="form-control"
                    autocomplete="new-username" />
                  <span class="status-icon">
                    <span *ngIf="isCheckingUserName" class="spinner-grow nx-spinner" role="status"
                      style="width: 18px; height: 18px"></span>
                    <i *ngIf="userNameValid === true" class="bi bi-check-circle-fill text-success"></i>
                    <i *ngIf="userNameValid === false" class="bi bi-x-circle-fill text-danger"></i>
                  </span>
                </div>
                <small class="text-danger" *ngIf="reviewForm.get('userName')?.hasError('required') && reviewForm.get('userName')?.touched">
                  Username is required.
                </small>
                <small class="text-danger" *ngIf="userNameError">{{ userNameError }}</small>
              </div>

              <!-- Password -->
              <div class="credentials-group">
                <label>Password <span class="req">*</span></label>
                <div class="input-with-status">
                  <input [type]="showPassword ? 'text' : 'password'" formControlName="userPassword"
                    placeholder="Enter Password" autocomplete="new-password" class="form-control" />
                  <i class="bi eye-toggle" [ngClass]="showPassword ? 'bi-eye-slash' : 'bi-eye'"
                    (click)="togglePasswordVisibility()"></i>
                </div>
                <small class="text-danger" *ngIf="reviewForm.get('userPassword')?.invalid && reviewForm.get('userPassword')?.touched">
                  <span *ngIf="reviewForm.get('userPassword')?.hasError('required')">Password is required.</span>
                  <span *ngIf="reviewForm.get('userPassword')?.hasError('minlength')">Password must be at least 6 characters long.</span>
                </small>
              </div>
            </div>

            <div class="d-flex align-items-center text-secondary small mt-3">
              <i class="bi bi-info-circle me-2 text-primary"></i>
              <span>The user gets the <strong>{{ roleLabel(selectedRequest?.affiliationType || '') }}</strong> access role automatically on approval.</span>
            </div>
          </div>

        </div>

        <!-- Footer -->
        <div class="modal-footer mt-2">
          <button type="button" class="btn btn-secondary nx-close-btn" (click)="closeModal()" [disabled]="isSubmitting">
            Close
          </button>
          <button *ngIf="!isViewOnly" type="submit" class="btn nx-approve-btn"
            [disabled]="isSubmitting || reviewForm.invalid || userNameValid === false || isCheckingUserName">
            <span *ngIf="!isSubmitting"><i class="bi bi-check2-circle me-1"></i> Approve &amp; Grant Access</span>
            <span *ngIf="isSubmitting"><span class="spinner-border spinner-border-sm me-1"></span> Approving...</span>
          </button>
        </div>
      </form>
    </div>
  </ng-template>

</div>